import type { TEnumItem } from '@modules/types'
import { REQUEST_TYPE, DISTRIBUTION_METHOD } from './enums'

export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

export const convertSnakeToCamel = <T>(obj: unknown): T => {
  if (obj === null || obj === undefined) {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeToCamel) as T
  }

  if (typeof obj === 'object') {
    const converted = Object.entries(obj as Record<string, unknown>).reduce(
      (acc, [key, value]) => {
        let camelKey: string
        if (key === '_meta') {
          camelKey = 'meta'
        } else if (key.startsWith('_')) {
          camelKey = key
        } else {
          camelKey = snakeToCamel(key)
        }
        acc[camelKey] = convertSnakeToCamel(value)
        return acc
      },
      {} as Record<string, unknown>,
    )
    return converted as T
  }

  return obj as T
}

export const convertCamelToSnake = <T>(obj: unknown): T => {
  if (obj === null || obj === undefined) {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map(convertCamelToSnake) as T
  }

  if (typeof obj === 'object') {
    const converted = Object.entries(obj as Record<string, unknown>).reduce(
      (acc, [key, value]) => {
        const snakeKey = camelToSnake(key)
        acc[snakeKey] = convertCamelToSnake(value)
        return acc
      },
      {} as Record<string, unknown>,
    )
    return converted as T
  }

  return obj as T
}

export const convertApiResponse = <T>(response: unknown): T => {
  return convertSnakeToCamel<T>(response)
}

export const getEnumNameById = (enumList: TEnumItem[], enumName: string, id: number): string => {
  return enumList.find(item => item.enumName === enumName && item.id === id)?.name || 'Unknown'
}

export const isPrintOnly = (distributionMethod?: number): boolean => {
  return distributionMethod === DISTRIBUTION_METHOD.PRINT_ONLY
}

export const isDigitalOnly = (distributionMethod?: number): boolean => {
  return distributionMethod === DISTRIBUTION_METHOD.DIGITAL_ONLY
}

export const isBoth = (distributionMethod?: number): boolean => {
  return distributionMethod === DISTRIBUTION_METHOD.BOTH
}

export const includesPrint = (distributionMethod: number): boolean => {
  return (
    distributionMethod === DISTRIBUTION_METHOD.PRINT_ONLY ||
    distributionMethod === DISTRIBUTION_METHOD.BOTH
  )
}

export const includesDigital = (distributionMethod: number): boolean => {
  return (
    distributionMethod === DISTRIBUTION_METHOD.DIGITAL_ONLY ||
    distributionMethod === DISTRIBUTION_METHOD.BOTH
  )
}

export const isApprovalRequest = (requestType?: number): boolean => {
  return requestType === REQUEST_TYPE.APPROVAL
}

export const isCopyrightRequest = (requestType?: number): boolean => {
  return requestType === REQUEST_TYPE.COPYRIGHT
}

export const groupValidationErrorsByField = (rawErrors: unknown): Record<string, string[]> => {
  if (!rawErrors || typeof rawErrors !== 'object') return {}

  return Object.entries(rawErrors as Record<string, unknown>).reduce<Record<string, string[]>>(
    (acc, [key, value]) => {
      const field = key.includes('.') ? key.split('.')[0] : key
      let messages: string[] = []
      if (Array.isArray(value)) {
        messages = value.filter((item): item is string => typeof item === 'string')
      } else if (typeof value === 'string') {
        messages = [value]
      }

      if (messages.length === 0) return acc
      acc[field] = [...(acc[field] || []), ...messages]
      return acc
    },
    {},
  )
}

export const extractRawValidationErrors = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') return undefined
  if ('errors' in error) return (error as { errors?: unknown }).errors
  if ('data' in error) return (error as { data?: { errors?: unknown } }).data?.errors
  return undefined
}

export const getUniqueValidationErrors = (errors: string[]): string[] => {
  return Array.from(new Set(errors))
}
